import {
  createFileRoute,
  useNavigate,
  useSearch
} from '@tanstack/react-router';
import { Loader2, CheckCircle, XCircle, LogIn } from 'lucide-react';
import * as React from 'react';

import { Button } from '~/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle
} from '~/components/ui/card';
import { useAppSettings } from '~/hooks/use-app-settings';
import { useAuth } from '~/lib/auth';

export const Route = createFileRoute('/auth/verify')({
  component: VerifyPage
});

function VerifyPage() {
  const search = useSearch({ from: '/auth/verify' }) as { token?: string };
  const navigate = useNavigate();
  const { login } = useAuth();
  const { data: settings } = useAppSettings();
  const _companyName = settings?.companyName ?? 'Open Dataroom';
  const [status, setStatus] = React.useState<
    'idle' | 'verifying' | 'success' | 'error'
  >('idle');
  const [error, setError] = React.useState('');

  const token = search.token;

  const handleConfirm = React.useCallback(async () => {
    if (!token) {
      setStatus('error');
      setError('No token provided');
      return;
    }
    setStatus('verifying');
    try {
      await login(token);
      window.history.replaceState(null, '', '/auth/verify');
      setStatus('success');
      setTimeout(() => navigate({ to: '/dashboard' }), 1500);
    } catch (err: unknown) {
      setStatus('error');
      setError(err instanceof Error ? err.message : 'Verification failed');
    }
  }, [token, login, navigate]);

  return (
    <div className="flex min-h-screen items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <CardTitle>
            {status === 'idle' && 'Confirm Sign In'}
            {status === 'verifying' && 'Verifying...'}
            {status === 'success' && 'Welcome!'}
            {status === 'error' && 'Verification Failed'}
          </CardTitle>
          <CardDescription>
            {status === 'idle' &&
              `Click below to confirm and sign in to ${_companyName}.`}
            {status === 'verifying' && "We're confirming your magic link."}
            {status === 'success' && "You've been signed in successfully."}
            {status === 'error' && error}
          </CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col items-center py-6">
          {status === 'verifying' && (
            <Loader2 className="h-12 w-12 animate-spin text-primary" />
          )}
          {status === 'success' && (
            <CheckCircle className="h-12 w-12 text-green-500" />
          )}
          {status === 'error' && (
            <XCircle className="h-12 w-12 text-destructive" />
          )}

          {status === 'idle' && (
            <Button className="mt-2" onClick={handleConfirm}>
              <LogIn className="mr-2 h-4 w-4" />
              Confirm and Sign In
            </Button>
          )}

          {status === 'error' && (
            <Button className="mt-4" onClick={() => navigate({ to: '/login' })}>
              Back to Sign In
            </Button>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
